'use client'
import { type FC, memo, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { getLocaleFromPathname } from '@/proxy'
import { HOSTNAME } from '@/utils'
import type { TypeCmsContentMedia, TypeCmsMedium, TypeCmsVideo } from '@/schema'
import { getP50, mq, vw } from '@/styles'
import { Link } from '../Link'
import { VideoGeneric } from '../VideoGeneric'
import { Img } from '../Img'
import { getFlexAlign, type TypeFlexAlign } from './ContentText'
import { usePathname } from 'next/navigation'

type Props = {
  data: TypeCmsContentMedia
  contentsPerRow?: number
}

type DivProps = {
  $col: TypeCmsContentMedia['col']
  $orientation: TypeCmsContentMedia['medium_orientation']
  $fullscreen?: TypeCmsContentMedia['fullscreen']
  $contentsPerRow?: number
  $textPosition: TypeFlexAlign
}

export const getHrefFromCMSUrl = (link_path) => {
  if(typeof link_path !== 'string') return null
  const pathnameSplitted = link_path.split('https://' + HOSTNAME)
  const pathname = pathnameSplitted[pathnameSplitted?.length > 1 ? 1 : 0]

  const localeFromPathname = getLocaleFromPathname(pathname)

  if(localeFromPathname) {
    const href = pathname.replace(`/${localeFromPathname}`, '')
    return href
  }

  return pathname
}

export function isMediumImage(obj:unknown): obj is TypeCmsMedium {
  return obj && typeof obj === 'object' && 'image' in obj
}

export const ContentMedia: FC<Props> = memo(({ data, contentsPerRow }) => {
  const { id, col, fullscreen, link_path, hover_text, link_title, medium, medium_orientation, text, text_position } = data
  const href = getHrefFromCMSUrl(link_path)
	const [showHoverText, setShowHoverText] = useState(false)

	const pathname = usePathname()
	const isSpectralEchoes = useMemo(() => pathname.includes('/spectral-echoes'), [pathname])

  return (
    <>
      <Div data-id={id} className={`ct ct--media ${isSpectralEchoes ? 'spectral-echoes' : ''} ${hover_text ? 'hover-text' : ''} ${medium_orientation}`} $col={col} $orientation={medium_orientation} $contentsPerRow={contentsPerRow} $fullscreen={fullscreen} $textPosition={getFlexAlign(text_position)} onMouseOver={() => setShowHoverText(true)} onMouseLeave={() => setShowHoverText(false)}>
        {href ?
          <>
            {href?.[0] === '/' ?
              <Link href={href}>
                {isMediumImage(medium) ? <Img className='media' media={medium.image} /> : <VideoGeneric data={medium as TypeCmsVideo} />}
                {link_title && <span>{link_title}</span>}
              </Link>
              :
              <a href={href} target='_blank' rel='noreferrer'>
                {isMediumImage(medium) ? <Img className='media' media={medium?.image} /> : <VideoGeneric data={medium as TypeCmsVideo} />}
                {link_title && <span>{link_title}</span>}
              </a>
            }
          </>
          :
          <>{isMediumImage(medium) ? <Img className='media' media={medium.image} /> : <VideoGeneric data={medium as TypeCmsVideo} />}</>
        }
        {text && <div className='md' dangerouslySetInnerHTML={{ __html: text }} />}
				{hover_text && <div className={`wrapper-hover-text ${showHoverText ? ' hover' : ''}`} dangerouslySetInnerHTML={{ __html: hover_text }} onClick={() => setShowHoverText(false)}/>}
				{hover_text && <button className='btn--hover-text' onClick={() => { setShowHoverText(true); console.log('showHoverText:', showHoverText)}}>
					<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26' stroke='#ffffff' fill='none' viewBox='0 0 26 26'><g strokeLinecap='square' strokeWidth='.5'><path d='M13 7.55v10.91'/><path d='M7 13h12'/></g><path d='M13 1a12 12 0 1 0 0 24 12 12 0 1 0 0-24' strokeWidth='.5'/></svg>
					</button>}
      </Div>
    </>
  )
})

const heightByProps = {
  'fullscreen': css`
    height: ${vw(233, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(233, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(810, 'desktop')};
    }
  `,
  'landscape-1': css`
    height: ${vw(245, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(245, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(597, 'desktop')};
    }
  `,
  'landscape-n': css`
    height: ${vw(115, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(115, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(367, 'desktop')};
    }
  `,
  'portrait-1': css`
    height: ${vw(354, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(354, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(823, 'desktop')};
    }
  `,
  'portrait-n': css`
    height: ${vw(257, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(257, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(650, 'desktop')};
    }
  `
}

const Div = styled.div<DivProps>`
  ${({ $fullscreen, $orientation, $contentsPerRow }) => heightByProps[$fullscreen ? 'fullscreen' : `${$orientation}-${$contentsPerRow > 1 ? 'n' : '1'}`]};
  margin: 0 auto;
  position: relative;
  width: 100%;

  &.ct.ct--media {
		align-items: flex-start;
		justify-content: flex-start;
		flex-direction: column;

    ${mq.lessThan('tablet')} {
      grid-column: ${({ $orientation, $contentsPerRow }) => $contentsPerRow > 1 ? 'auto / span 3' : $orientation === 'portrait' ? '2 / span 4' : '1 / span 6'};
      margin-bottom: ${({ $contentsPerRow }) => $contentsPerRow > 1 ? '0' : 0};
    }

		&.hover-text {
			.btn--hover-text {
				align-items: center;
				display: flex;
				filter: invert(1);
				height: 25px;
				opacity: .6;
				justify-content: center;
				position: absolute;
				right: 0;
				top: calc(100% - 25px);
				width: 25px;
				z-index: 10;

				${mq.greaterThan('tablet')} {
					display: none;
				}

				svg {
					height: 20px;
					width: 20px;
				}
			}

			.wrapper-hover-text {
				background-color: rgba(0, 0, 0, .5);
				content: '';
				color: white;
				left: 0;
				opacity: 0;
				position: absolute;
				font-size: 10px !important;
				line-height: 109% !important;
				padding: 15px;
				top: 0;
				transition: opacity 300ms ease;
				user-select: none;
				width: 100%;

				${mq.greaterThan('tablet')} {
					font-size: 13px !important;
				}

				p, ul, ol, dl, li, dd {
					font-size: 10px !important;
					line-height: 109% !important;

					${mq.greaterThan('tablet')} {
						font-size: 13px !important;
					}
				}

				&.hover {
					opacity: 1;
					z-index: 12;
				}
			}

			&:hover {
				opacity: 1;
			}
		}

		&.spectral-echoes {
			.md {
				margin-top: 10px;
				color: var(--color-text);
				font-size: 16px !important;
				height: auto;
				line-height: 109% !important;
				position: static;
				padding: 0 !important;
				top: 100%;

				p {
					font-size: 16px !important;
					line-height: 109% !important;
					font-weight: 300 !important;
					margin-right: auto;
				}
			}
		}
  }


  ${mq.greaterThan('tablet')} {
    grid-column: ${({ $col }) => `${$col.from} / span ${$col.expand}`};
  }

  .md {
    align-items: center;
    color: var(--color-white);
    display: flex;
    height: 100%;
    justify-content: center;
    padding: 0 10%;
    pointer-events: none;
    position: absolute;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      justify-content: ${({ $textPosition }) => $textPosition ?? 'center'};
    }

    a, button, ul, ol, dl, p {
      ${getP50()}
      font-weight: 300;
    }
  }

  a {
    align-items: center;
    color: var(--color-white);
    display: grid;
    height: 100%;
    justify-content: center;
    overflow: hidden;
    width: 100%;

    span,
    .media {
      grid-row: 1 / span 1;
      grid-column: 1 / span 1;


    }

    span {
      ${getP50()}
      font-weight: 300;
      text-align: center;
      width: 100%;
      z-index: 2;
    }

    &:hover {
      .media {
        transform: scale(1.1);
      }
    }
  }

  .media,
  .img,
  img {
    background-color: var(--color-img-placeholder);
    height: 100%;
    object-fit: cover;
    transition: 1000ms transform var(--transition-ease);
    width: 100%;
  }
`